import { EDITOR_SYSTEM_VARIABLE_NAMES as SYSTEM_VARIABLE_NAMES } from 'utils/variableMetadata.generated';
import { splitNamespace } from 'utils/templateVariableParsing';
import {
  lookupDeprecatedReplacement,
  type DeprecatedVariables,
} from 'utils/variableValuePrimitives';
import {
  getBuiltinVariableLookupPolicy,
  resolveVariableLookupPolicy,
} from 'utils/variableValueResolvers';

/**
 * Look up a placeholder operand in the editor's resolved-value map.
 *
 * Built-ins use the shared backend lookup policies. For ordinary ATS attributes,
 * `job.<tail>` aliases the un-namespaced key (`job.location.city` → `location.city`).
 * An explicit namespaced ATS key wins even when it is empty.
 */
export const lookupTemplateVariableValue = (
  name: string,
  variableValues: Record<string, string> | null | undefined,
  deprecatedVariables?: DeprecatedVariables
): string | undefined => {
  if (!variableValues) {
    return undefined;
  }
  const trimmed = name.trim();
  const policy = getBuiltinVariableLookupPolicy(trimmed);
  if (policy) {
    return resolveVariableLookupPolicy(variableValues, policy);
  }
  if (Object.prototype.hasOwnProperty.call(variableValues, trimmed)) {
    return variableValues[trimmed];
  }
  // Check if it's a deprecated variable and lookup its canonical replacement
  const replacement = lookupDeprecatedReplacement(trimmed, deprecatedVariables);
  if (replacement) {
    const replacedValue = lookupTemplateVariableValue(
      replacement,
      variableValues
    );
    if (replacedValue !== undefined) {
      return replacedValue;
    }
  }
  const split = splitNamespace(trimmed);
  if (split?.prefix !== 'job') {
    return undefined;
  }
  // job.* aliases flattened ATS keys only — never system vars (`job.job_name` →
  // `job_name`) or another namespace (`job.candidate.first_name`).
  if (
    SYSTEM_VARIABLE_NAMES.has(split.tail) ||
    splitNamespace(split.tail) !== null
  ) {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(variableValues, split.tail)) {
    return variableValues[split.tail];
  }
  return undefined;
};
